import ActBadges from '../../components/ui/ActBadges'
import { useBack } from '../../navigation/navigation'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useParams } from 'react-router-dom'
import { useEntityNavigate as useNavigate, EntityLink as Link } from '../../navigation/navigation'
import api from '../../services/api'
import { useAuth } from '../../contexts/AuthContext'
import { usePageHeader } from '../../contexts/PageHeaderContext'
import ConfirmModal from '../../components/ConfirmModal'
import EntityComments from '../../components/EntityComments'
import EntityHistory from '../../components/EntityHistory'
import Icon from '../../components/Icon'
import {
  ActionMenu,
  Avatar,
  Breadcrumb,
  Card,
  DetailList,
  PageTitle,
  TableShell,
  Toast
} from '../../components/ui'
import { normalizeDate } from '../../utils/dateUtils'
import { formatRub, formatRubTable, parseAmount } from '../../utils/formatRub'
import { buildReturnState } from '../../utils/returnNavigation'
import { getBudgetExpenseKind, getBudgetExpenseMarker } from './budgetExpenseVisuals'
import './Company.css'

const PRODUCTION_SECTION = 'Производство'
const SERVICE_SECTION = 'Обслуживание'

function buildActDistributionHierarchy(distributionByTeam) {
  const deptTeams = {}
  const deptMeta = {}
  for (const group of distributionByTeam || []) {
    const team = (group.team || '').trim() || 'Без команды'
    for (const emp of group.employees || []) {
      const dept = (emp.department || '').trim() || 'Без подразделения'
      if (!deptMeta[dept]) {
        deptMeta[dept] = {
          department_type: emp.department_type || group.department_type || '',
        }
      }
      if (!deptTeams[dept]) deptTeams[dept] = {}
      if (!deptTeams[dept][team]) {
        deptTeams[dept][team] = {
          team_name: team,
          distributionMismatch: Boolean(group.distribution_mismatch),
          employees: [],
        }
      }
      deptTeams[dept][team].employees.push(emp)
    }
  }

  return Object.entries(deptTeams)
    .map(([department_name, teamsObj]) => ({
      department_name,
      section: getActDepartmentSection(department_name, deptMeta[department_name]?.department_type),
      teams: Object.entries(teamsObj)
        .map(([, teamGroup]) => ({
          ...teamGroup,
          employees: [...teamGroup.employees].sort((a, b) =>
            (a.employee_name || '').localeCompare(b.employee_name || '', 'ru')
          ),
        }))
        .sort((a, b) => a.team_name.localeCompare(b.team_name, 'ru')),
    }))
    .sort((a, b) => (
      getSectionOrder(a.section) - getSectionOrder(b.section)
      || a.department_name.localeCompare(b.department_name, 'ru')
    ))
}

function flattenDistributionRows(distributionHierarchy, actAmount) {
  return distributionHierarchy.map((dept) => {
    const rows = []
    for (const team of dept.teams || []) {
      for (const emp of team.employees || []) {
        const amount = parseAmount(emp.amount) ?? 0
        const rolePercent = parseAmount(emp.employee_pct_in_team)
        const roleLabel = [
          team.team_name,
          String(emp.sub_team || '').trim(),
          rolePercent === null ? '' : formatPercent(rolePercent),
        ].filter(Boolean).join(' · ')
        rows.push({
          key: `${dept.department_name}-${team.team_name}-${emp.employee_id || emp.employee_name || rows.length}-${emp.sub_team || ''}`,
          department: dept.department_name,
          team: team.team_name,
          roleLabel,
          employeeId: emp.employee_id,
          employeeName: emp.employee_name || '-',
          amount,
          percentOfAct: actAmount > 0 ? (amount / actAmount) * 100 : 0,
          teamMismatch: Boolean(team.distributionMismatch),
        })
      }
    }
    const total = rows.reduce((sum, row) => sum + row.amount, 0)
    return {
      department: dept.department_name,
      section: dept.section,
      rowSpan: Math.max(rows.length, 1),
      total,
      percentOfAct: actAmount > 0 ? (total / actAmount) * 100 : 0,
      rows,
    }
  })
}

function formatStageNumberLabel(actData) {
  const contractNumber = (actData?.contract_number || '').trim()
  const stageType = (
    actData?.stage_distribution_type ||
    actData?.distribution_type ||
    actData?.stage_type ||
    actData?.type ||
    ''
  ).trim()
  if (contractNumber && stageType) return `${contractNumber} - ${stageType}`
  const sid = String(actData?.stage_id_display || actData?.stage_id || '').trim()
  if (sid) return sid
  const fallback = (actData?.stage_display_name || actData?.stage_name || '').trim()
  return fallback || '-'
}

function formatDate(dateString, options = {}) {
  if (!dateString) return '-'
  const date = new Date(dateString)
  if (Number.isNaN(date.getTime())) return '-'
  return date.toLocaleDateString('ru-RU', options)
}

function formatDateLong(dateString) {
  return formatDate(dateString, { day: 'numeric', month: 'long', year: 'numeric' })
}

function formatPercent(value) {
  if (!Number.isFinite(value)) return '0%'
  if (value === 100) return '100%'
  return `${value.toFixed(3).replace(/\.?0+$/, '').replace('.', ',')}%`
}

function formatActTotalPercent(value) {
  if (!Number.isFinite(value)) return '0%'
  if (value === 100) return '100%'
  return `${value.toFixed(4).replace(/\.?0+$/, '').replace('.', ',')}%`
}

function formatRubNumber(raw) {
  const value = parseAmount(raw)
  if (value === null) return '0'
  return new Intl.NumberFormat('ru-RU', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(value)
}

function getActDepartmentSection(department, departmentType) {
  if (String(departmentType || '').trim() === 'Производственное') return PRODUCTION_SECTION
  const text = String(department || '').toLowerCase()
  if (text.includes('бух') || text.includes('пред') || text.includes('торг') || text.includes('выпуск') || text.includes('айти')) {
    return SERVICE_SECTION
  }
  return PRODUCTION_SECTION
}

function getSectionOrder(section) {
  return section === PRODUCTION_SECTION ? 0 : 1
}

function buildOtherDistributionRows(items, amountForDistribution) {
  return (items || [])
    .map((item, index) => {
      const amount = parseAmount(item?.amount) ?? 0
      const label = String(item?.label || item?.team || '').trim() || 'Остальная статья'
      const expenseKind = getBudgetExpenseKind(item)
      return {
        key: `${item?.distribution_category || 'other'}-${label}-${index}`,
        label,
        expenseKind,
        amount,
        percentOfAct: amountForDistribution > 0 ? (amount / amountForDistribution) * 100 : 0,
      }
    })
}

function getStatusVariant(status) {
  const normalized = String(status || '').toLowerCase().trim()
  if (['подписан', 'оплачен', 'signed', 'paid'].includes(normalized)) return 'signed'
  if (normalized === 'на подписании') return 'pending'
  return 'unsigned'
}

function getInitials(name) {
  const parts = String(name || '')
    .split(/\s+/)
    .filter(Boolean)
  if (!parts.length) return '—'
  const [last, first] = parts
  return `${last?.[0] || ''}${first?.[0] || ''}`.toUpperCase()
}

function getDepartmentTone(department) {
  const text = String(department || '').toLowerCase()
  if (text.includes('ар') || text.includes('арх')) return 'arch'
  if (text.includes('кж') || text.includes('констр') || text.includes('инж')) return 'eng'
  if (text.includes('диз')) return 'des'
  if (text.includes('пр') || text.includes('управ')) return 'mgmt'
  if (text.includes('фин') || text.includes('бух')) return 'fin'
  return 'default'
}

function getDepartmentLetter(department) {
  const trimmed = String(department || '').trim()
  return trimmed ? trimmed[0].toUpperCase() : '—'
}

function getFilenameFromDisposition(contentDisposition, fallback) {
  const header = String(contentDisposition || '')
  const utfMatch = header.match(/filename\*=UTF-8''([^;]+)/i)
  if (utfMatch?.[1]) {
    try {
      return decodeURIComponent(utfMatch[1].replace(/^["']|["']$/g, ''))
    } catch (_) {
      return utfMatch[1].replace(/^["']|["']$/g, '').trim() || fallback
    }
  }
  const match = header.match(/filename=["']?([^"';]+)["']?/i)
  return match?.[1]?.trim() || fallback
}

function downloadBlob(data, filename) {
  const blob = data instanceof Blob ? data : new Blob([data], { type: 'application/pdf' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  link.remove()
  URL.revokeObjectURL(url)
}

function ActCard() {
  const back = useBack('/company/acts')
  const { id } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const { user } = useAuth()
  const toastTimerRef = useRef(null)
  usePageHeader({ title: null, hideTopHeader: true }, [])

  const canUpdate = Boolean(user?.permissions?.['acts.update'])
  const canDelete = Boolean(user?.permissions?.['acts.delete'])
  const [act, setAct] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [signing, setSigning] = useState(false)
  const [deleteModalOpen, setDeleteModalOpen] = useState(false)
  const [deleteError, setDeleteError] = useState(null)
  const [deleting, setDeleting] = useState(false)
  const [pdfLoading, setPdfLoading] = useState(false)
  const [toastMessage, setToastMessage] = useState('')

  const fetchAct = async () => {
    try {
      setLoading(true)
      setError(null)
      const response = await api.get(`/company/acts/${id}`)
      if (response.data?.act) {
        setAct(response.data.act)
      } else {
        setError('Акт не найден')
      }
    } catch (err) {
      console.error('Error fetching act:', err)
      setError(err.response?.status === 404 ? 'Акт не найден' : 'Не удалось загрузить данные')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchAct()
  }, [id])

  useEffect(() => () => {
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current)
  }, [])

  const actAmount = parseAmount(act?.amount) ?? 0
  const amountAfterTax = parseAmount(act?.amount_after_tax) ?? 0
  const amountForDistribution = amountAfterTax || actAmount
  const distributionByTeam = act?.distribution_by_team || []
  const distributionHierarchy = useMemo(
    () => buildActDistributionHierarchy(distributionByTeam),
    [distributionByTeam]
  )
  const distributionGroups = useMemo(
    () => flattenDistributionRows(distributionHierarchy, amountForDistribution),
    [distributionHierarchy, amountForDistribution]
  )
  const distributionRows = distributionGroups.flatMap((group) => group.rows)
  const otherDistributionRows = useMemo(
    () => buildOtherDistributionRows(act?.non_employee_distribution_items, amountForDistribution),
    [act?.non_employee_distribution_items, amountForDistribution]
  )
  const calculatedOtherDistributedTotal = otherDistributionRows.reduce((sum, row) => sum + row.amount, 0)
  const employeeDistributedTotal =
    parseAmount(act?.employee_distribution_sum) ??
    distributionRows.reduce((sum, row) => sum + row.amount, 0)
  const totalDistributed =
    parseAmount(act?.distribution_sum) ??
    employeeDistributedTotal + calculatedOtherDistributedTotal
  const otherDistributedTotal =
    parseAmount(act?.non_employee_distribution_sum) ??
    (calculatedOtherDistributedTotal > 0
      ? calculatedOtherDistributedTotal
      : Math.max(totalDistributed - employeeDistributedTotal, 0))
  const distributionDifference = amountForDistribution - totalDistributed
  const hasDistributionShortage = amountForDistribution > 0 && distributionDifference > 1
  const hasDistributionOverage = amountForDistribution > 0 && distributionDifference < -1
  const isDistributionMismatch =
    Boolean(act?.distribution_mismatch) || hasDistributionShortage || hasDistributionOverage
  const employeesCount = distributionRows.length
  const teamsCount = new Set(distributionRows.map((row) => row.team)).size
  const distributionNoticeVariant = isDistributionMismatch
    ? 'is-error'
    : amountForDistribution > 0
      ? 'is-complete'
      : 'is-warning'
  const isDistributionComplete = distributionNoticeVariant === 'is-complete'
  const hasDistributionRows = distributionRows.length > 0 || otherDistributionRows.length > 0
  const actTitle = `Акт №${act?.number || act?.id || id}`
  const stageLabel = formatStageNumberLabel(act)
  const stageName = act?.stage_display_name || act?.stage_name || act?.stage_id || '-'
  const isSigned = getStatusVariant(act?.status) === 'signed'

  const handleSetSigned = async () => {
    if (!canUpdate || !act || signing || isSigned) return
    setSigning(true)
    setError(null)
    try {
      await api.put(`/company/acts/${id}`, {
        contract_id: act.contract_id,
        stage_id: act.stage_id,
        number: act.number,
        issue_date: normalizeDate(act.issue_date) || null,
        amount: act.amount,
        amount_after_tax: act.amount_after_tax,
        period: act.period || null,
        status: 'подписан',
        custom_data: act.custom_data || {}
      })
      await fetchAct()
    } catch (err) {
      console.error('Error updating act status:', err)
      setError(err.response?.data?.error || 'Не удалось изменить статус')
    } finally {
      setSigning(false)
    }
  }

  const handleDelete = async () => {
    if (!canDelete || deleting) return
    setDeleting(true)
    setDeleteError(null)
    try {
      await api.delete(`/company/acts/${id}`)
      navigate('/company/acts')
    } catch (err) {
      console.error('Error deleting act:', err)
      setDeleteError(err.response?.data?.error || 'Не удалось удалить акт')
    } finally {
      setDeleting(false)
    }
  }

  const showToast = (message) => {
    setToastMessage(message)
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current)
    toastTimerRef.current = setTimeout(() => {
      setToastMessage('')
      toastTimerRef.current = null
    }, 2200)
  }

  const handleDownloadPdf = async () => {
    if (!act || pdfLoading) return
    setPdfLoading(true)
    setError(null)
    showToast('PDF готовится…')
    try {
      const response = await api.get(`/company/acts/${id}/pdf`, { responseType: 'blob' })
      const fallbackName = `act-${String(act.number || id || 'report').replace(/[^a-zA-Z0-9_-]/g, '_')}-${new Date().toISOString().slice(0, 10)}.pdf`
      const filename = getFilenameFromDisposition(response.headers?.['content-disposition'], fallbackName)
      downloadBlob(response.data, filename)
    } catch (err) {
      console.error('Error downloading act PDF:', err)
      showToast('Не удалось скачать PDF')
      setError(err.response?.data?.error || 'Не удалось скачать PDF')
    } finally {
      setPdfLoading(false)
    }
  }

  if (loading) {
    return (
      <div className="company-page act-detail-page">
        <div className="loading">Загрузка...</div>
      </div>
    )
  }

  if (error || !act) {
    return (
      <div className="company-page act-detail-page">
        <button type="button" className="act-detail-back-link" onClick={back}>
          Назад
        </button>
        <p className="info-note">{error || 'Акт не найден'}</p>
      </div>
    )
  }

  const breadcrumbItems = [
    { label: 'Главная', to: '/' },
    { label: 'Договоры', to: '/company/contracts' },
    act.contract_id && { label: act.contract_number || 'Договор', to: `/contract/${act.contract_id}` },
    { label: actTitle },
  ].filter(Boolean)

  const actionItems = [
    canUpdate && {
      id: 'edit',
      label: 'Редактировать акт',
      icon: <Icon name="edit" size={14} />,
      onSelect: () => navigate(`/company/acts/${id}/edit`, {
        state: buildReturnState(location, '← Назад к акту'),
      }),
    },
    (act.payment_type || 'bank_transfer') === 'bank_transfer' && {
      id: 'download-pdf',
      label: pdfLoading ? 'PDF готовится...' : 'Скачать PDF',
      icon: <Icon name="download" size={14} />,
      disabled: pdfLoading,
      onSelect: handleDownloadPdf,
    },
    canUpdate && (act.payment_type || 'bank_transfer') === 'bank_transfer' && {
      id: 'signed',
      label: signing ? 'Сохранение...' : 'Отметить подписанным',
      icon: <Icon name="check" size={14} />,
      disabled: signing || isSigned,
      onSelect: handleSetSigned,
    },
    canUpdate && canDelete && { id: 'divider-delete', type: 'divider' },
    canDelete && {
      id: 'delete',
      label: 'Удалить акт',
      icon: <Icon name="delete" size={14} />,
      danger: true,
      onSelect: () => setDeleteModalOpen(true),
    },
  ].filter(Boolean)

  return (
    <div className="company-page act-detail-page entity-detail-page">
      <Breadcrumb items={breadcrumbItems} className="act-detail-breadcrumb" />

      <header className="act-detail-header">
        <div className="act-detail-header__left">
          <div className="act-detail-icon" aria-hidden>
            <Icon name="act" size={22} />
          </div>
          <div>
            <PageTitle className="act-detail-title">{actTitle}</PageTitle>
            <p className="act-detail-subtitle">
              {[act.object_name, act.contract_number && `${act.contract_number} — ${stageName}`, act.issue_date && `${act.status || 'дата'} ${formatDateLong(act.issue_date)}`]
                .filter(Boolean)
                .join(' · ')}
            </p>
          </div>
        </div>
        <div className="act-detail-header__right">
          <ActBadges act={act} />
          <ActionMenu items={actionItems} />
        </div>
      </header>

      {error && <div className="message message-error" role="alert">{error}</div>}

      <section className="act-detail-hero" aria-labelledby="act-detail-hero-title">
        <div className="act-detail-hero__overline" id="act-detail-hero-title">
          Сумма {actTitle}{act.period ? ` · ${act.period}` : ''}
        </div>
        <div className="act-detail-hero__amount">
          <span>{formatRubNumber(actAmount)}</span>
          <span className="act-detail-hero__currency">₽</span>
        </div>
        {act.distribution_eligible === false ? (
          <div className="act-detail-hero__notice" role="status">
            {act.distribution_exclusion_reason}
          </div>
        ) : <div className={`act-detail-hero__notice ${distributionNoticeVariant}`}>
          <Icon name={isDistributionComplete ? 'check' : 'warning'} size={18} />
          <span>
            {(act.payment_type || 'bank_transfer') === 'bank_transfer' ? 'За вычетом НДС' : 'Сумма к распределению'} <strong>{formatRub(amountForDistribution)}</strong>:{' '}
            {employeesCount > 0 ? (
              <>
                сотрудникам <strong>{formatRub(employeeDistributedTotal, { showZero: true })}</strong> ({employeesCount})
              </>
            ) : (
              <>распределение по сотрудникам не найдено</>
            )}
            {otherDistributedTotal > 1 && (
              <>
                , остальное <strong>{formatRub(otherDistributedTotal, { showZero: true })}</strong>
              </>
            )}
            {hasDistributionShortage ? (
              <>
                . Не распределено <strong>{formatRub(distributionDifference, { showZero: true })}</strong>.
              </>
            ) : hasDistributionOverage ? (
              <>
                . Перераспределено на <strong>{formatRub(Math.abs(distributionDifference), { showZero: true })}</strong>.
              </>
            ) : isDistributionComplete ? (
              <>. Распределено полностью.</>
            ) : (
              <>. Проверьте распределение по сотрудникам.</>
            )}
          </span>
        </div>}
      </section>

      <Card className="act-detail-section">
        <Card.Header className="act-detail-section__head">
          <h2 className="ds-section-heading">Основные параметры</h2>
        </Card.Header>
        <DetailList>
          <DetailList.Item label="Договор">
            {act.contract_id ? (
              <Link to={`/contract/${act.contract_id}`} className="amount-mono">{act.contract_number || '-'}</Link>
            ) : (
              <span className="amount-mono">{act.contract_number || '-'}</span>
            )}
          </DetailList.Item>
          <DetailList.Item label="Клиент">
            {act.client_id ? <Link to={`/client/${act.client_id}`}>{act.client_name || '-'}</Link> : act.client_name || '-'}
          </DetailList.Item>
          <DetailList.Item label="Объект">
            {act.object_id ? <Link to={`/object/${act.object_id}`}>{act.object_name || '-'}</Link> : act.object_name || '-'}
          </DetailList.Item>
          <DetailList.Item label="Стадия">
            {act.stage_id ? (
              <>
                <Link to={`/stage/${encodeURIComponent(act.stage_id)}`} className="amount-mono">{stageLabel}</Link>
                <span className="ds-detail-list__muted">· {stageName}</span>
              </>
            ) : (
              stageLabel
            )}
          </DetailList.Item>
          <DetailList.Item label="Дата выставления">{formatDateLong(act.issue_date)}</DetailList.Item>
          <DetailList.Item label="Период">{act.period || '-'}</DetailList.Item>
          {(act.payment_type || 'bank_transfer') === 'bank_transfer' && <DetailList.Item label="Сумма за вычетом НДС">
            <span className="amount-mono">{formatRub(amountForDistribution)}</span>
          </DetailList.Item>}
        </DetailList>
      </Card>

      <Card className="act-detail-section act-detail-distribution">
        <Card.Header className="act-detail-section__head">
          <h2 className="ds-section-heading">Распределение по акту</h2>
          <span className="act-detail-section__meta">
            {employeesCount} сотрудников · {teamsCount} команд · сотрудникам {formatRub(employeeDistributedTotal, { showZero: true })}
            {' · '}из {formatRub(amountForDistribution)}
          </span>
        </Card.Header>

        {hasDistributionRows ? (
          <>
            <TableShell className="act-distribution-table-wrap">
              <table className="act-distribution-table">
                <colgroup>
                  <col className="act-distribution-table__col-dept" />
                  <col className="act-distribution-table__col-person" />
                  <col className="act-distribution-table__col-amount" />
                  <col className="act-distribution-table__col-percent" />
                </colgroup>
                <thead>
                  <tr>
                    <th>Подразделение</th>
                    <th>Исполнитель</th>
                    <th className="act-align-right">Сумма</th>
                    <th className="act-align-right">% от акта</th>
                  </tr>
                </thead>
                <tbody>
                  {distributionGroups.map((group, groupIndex) => (
                    <React.Fragment key={group.department}>
                      {group.section && group.section !== distributionGroups[groupIndex - 1]?.section ? (
                        <tr className="act-distribution-section-row act-distribution-section-row--main">
                          <td colSpan={4}>{group.section}</td>
                        </tr>
                      ) : null}
                      {group.rows.map((row, rowIndex) => (
                        <tr
                          key={row.key}
                          className={row.teamMismatch ? 'act-distribution-row--mismatch' : undefined}
                        >
                          {rowIndex === 0 && (
                            <td className="act-distribution-dept" rowSpan={group.rowSpan}>
                              <span className="act-category-tag">
                                <span className={`act-category-icon act-category-icon--${getDepartmentTone(group.department)}`}>
                                  {getDepartmentLetter(group.department)}
                                </span>
                                <span>{group.department}</span>
                              </span>
                            </td>
                          )}
                          <td>
                            <Link to={`/company/team/${encodeURIComponent(row.employeeId || row.employeeName)}`} className="act-person-link">
                              <Avatar size="sm">{getInitials(row.employeeName)}</Avatar>
                              <span className="act-person-text">
                                <span className="act-person-name">{row.employeeName}</span>
                                <span className="act-person-role">{row.roleLabel}</span>
                              </span>
                            </Link>
                          </td>
                          <td className="act-align-right">
                            <span className={`amount-mono${row.teamMismatch ? ' act-distribution-value--mismatch' : ''}`}>
                              {formatRub(row.amount)}
                            </span>
                          </td>
                          <td className="act-align-right">
                            <span className={`act-pct-value${row.teamMismatch ? ' act-distribution-value--mismatch' : ''}`}>
                              {formatPercent(row.percentOfAct)}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </React.Fragment>
                  ))}
                  <tr className="act-distribution-total act-distribution-total--employees">
                    <td colSpan={2}>Итого</td>
                    <td className="act-align-right amount-mono">{formatRub(employeeDistributedTotal, { showZero: true })}</td>
                    <td className="act-align-right"><span className="act-pct-value">{formatPercent(amountForDistribution > 0 ? (employeeDistributedTotal / amountForDistribution) * 100 : 0)}</span></td>
                  </tr>
                  {otherDistributionRows.length > 0 && (
                    <>
                      <tr className="act-distribution-section-row act-distribution-section-row--main">
                        <td colSpan={4}>{SERVICE_SECTION}</td>
                      </tr>
                      {otherDistributionRows.map((row) => (
                        <tr key={row.key} className={`act-distribution-other-row act-distribution-other-row--${row.expenseKind || 'other'}`}>
                          <td className="act-distribution-expense-name">
                            <span className={`act-expense-marker act-expense-marker--${row.expenseKind || 'other'}`} aria-hidden="true">
                              {getBudgetExpenseMarker(row.expenseKind)}
                            </span>
                            <span>{row.label}</span>
                          </td>
                          <td className="act-distribution-expense-empty" aria-hidden="true" />
                          <td className="act-align-right amount-mono">{formatRub(row.amount, { showZero: true })}</td>
                          <td className="act-align-right"><span className="act-pct-value">{formatPercent(row.percentOfAct)}</span></td>
                        </tr>
                      ))}
                    </>
                  )}
                  <tr className={`act-distribution-total act-distribution-total--act${isDistributionMismatch ? ' is-error' : ''}`}>
                    <td colSpan={2}>Итого по акту</td>
                    <td className="act-align-right amount-mono">{formatRub(totalDistributed, { showZero: true })}</td>
                    <td className="act-align-right">
                      <span className="act-pct-value">{formatActTotalPercent(amountForDistribution > 0 ? (totalDistributed / amountForDistribution) * 100 : 0)}</span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </TableShell>

            <div className="act-distribution-mobile-list">
              {distributionGroups.map((group) => (
                <section key={group.department} className="act-distribution-mobile-group">
                  <div className="act-distribution-mobile-group__head">
                    <span className="act-category-tag">
                      <span className={`act-category-icon act-category-icon--${getDepartmentTone(group.department)}`}>
                        {getDepartmentLetter(group.department)}
                      </span>
                      <span>{group.department}</span>
                    </span>
                    <span className="amount-mono">{formatRub(group.total)}</span>
                  </div>
                  {group.rows.map((row) => (
                    <Link
                      key={row.key}
                      to={`/company/team/${encodeURIComponent(row.employeeId || row.employeeName)}`}
                      className={`act-distribution-mobile-row${row.teamMismatch ? ' act-distribution-row--mismatch' : ''}`}
                    >
                      <Avatar size="sm">{getInitials(row.employeeName)}</Avatar>
                      <span className="act-person-text">
                        <span className="act-person-name">{row.employeeName}</span>
                        <span className="act-person-role">{row.roleLabel}</span>
                      </span>
                      <span className="act-distribution-mobile-row__amount">
                        <span className={`amount-mono${row.teamMismatch ? ' act-distribution-value--mismatch' : ''}`}>
                          {formatRub(row.amount)}
                        </span>
                        <span className={`act-pct-value${row.teamMismatch ? ' act-distribution-value--mismatch' : ''}`}>
                          {formatPercent(row.percentOfAct)}
                        </span>
                      </span>
                    </Link>
                  ))}
                </section>
              ))}
              {otherDistributionRows.length > 0 && (
                <section className="act-distribution-mobile-group">
                  <div className="act-distribution-mobile-group__head">
                    <span>{SERVICE_SECTION}</span>
                    <span className="amount-mono">{formatRub(otherDistributedTotal, { showZero: true })}</span>
                  </div>
                  {otherDistributionRows.map((row) => (
                    <div key={row.key} className={`act-distribution-mobile-row act-distribution-other-row--${row.expenseKind || 'other'}`}>
                      <span className="act-person-text">
                        <span className="act-person-name">{row.label}</span>
                      </span>
                      <span className="act-distribution-mobile-row__amount">
                        <span className="amount-mono">{formatRub(row.amount, { showZero: true })}</span>
                        <span className="act-pct-value">{formatPercent(row.percentOfAct)}</span>
                      </span>
                    </div>
                  ))}
                </section>
              )}
              <div className={`act-distribution-mobile-total${isDistributionMismatch ? ' is-error' : ''}`}>
                <span>Итого по акту</span>
                <span>
                  <span className="amount-mono">{formatRub(totalDistributed, { showZero: true })}</span>
                  <span className="act-pct-value">{formatActTotalPercent(amountForDistribution > 0 ? (totalDistributed / amountForDistribution) * 100 : 0)}</span>
                </span>
              </div>
            </div>
          </>
        ) : (
          <p className="info-note">{act.distribution_eligible === false
            ? 'Начисления появятся после подписания или оплаты акта.'
            : 'Нет данных о распределении по сотрудникам'}</p>
        )}
      </Card>

      <EntityComments entityType="acts" entityId={id} />
      <EntityHistory entityType="acts" entityId={id} />

      {deleteModalOpen && (
        <ConfirmModal
          title={`Удалить ${actTitle}?`}
          message={(
            <p>
              Акт будет архивирован, а распределение по сотрудникам удалится из расчёта.
              Подтверди удаление, если это точно нужно.
            </p>
          )}
          confirmLabel={deleting ? 'Удаление...' : 'Удалить'}
          cancelLabel="Отмена"
          confirmVariant="danger"
          confirmDisabled={deleting}
          error={deleteError}
          onCancel={() => {
            if (!deleting) {
              setDeleteModalOpen(false)
              setDeleteError(null)
            }
          }}
          onConfirm={handleDelete}
        />
      )}

      <Toast message={toastMessage} visible={Boolean(toastMessage)} />
    </div>
  )
}

export default ActCard
